import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  Avatar,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Alert,
  AlertTitle,
  Stepper,
  Step,
  StepLabel,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Container,
  useTheme,
  useMediaQuery,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Collapse,
  Checkbox,
  alpha,
  Autocomplete,
  Popper,
  LinearProgress,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  PersonAddOutlined,
  BusinessOutlined,
  StorefrontOutlined,
  PersonOutlineOutlined,
  EmailOutlined,
  LockOutlined,
  PhoneOutlined,
  HomeOutlined,
  AccountBalanceOutlined,
  CreditCardOutlined,
  PaymentsOutlined,
  ArrowBack,
  ArrowForward,
  CheckCircleOutline,
  Visibility,
  VisibilityOff,
  VerifiedUserOutlined,
  HowToRegOutlined,
  BadgeOutlined,
  ApartmentOutlined,
  MarkEmailReadOutlined,
  RefreshOutlined,
  VerifiedOutlined,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Error as ErrorIcon,
  LocationCityOutlined,
  MapOutlined,
} from '@mui/icons-material';

import logo from '../assets/images.png';
import Preloader from '../theme/Preloader';

// ============================================
// BRAND COLORS
// ============================================

const BRAND_COLORS = {
  primary: '#2E7D32',
  primaryLight: '#4CAF50',
  primaryDark: '#1B5E20',
  primaryPale: '#E8F5E9',
  secondary: '#C62828',
  accent: '#FF6F00',
  background: '#F5F7FA',
  surface: '#FFFFFF',
  text: '#1A1A1A',
  textSecondary: '#666666',
  border: '#E8ECF1',
  success: '#2E7D32',
  warning: '#ED6C02',
  error: '#D32F2F',
};

// ============================================
// API CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const API_ENDPOINTS = {
  REGISTER_SUPPLIER: '/suppliers/register_supplier.php',
  REGISTER_BUYER: '/buyers/register_buyer.php',
  RESEND_VERIFICATION: '/resend-verification.php',
  CHECK_VERIFICATION: '/check-verification.php',
  GET_BANKS: '/verify_bank_account.php?action=get_banks',
  VERIFY_BANK_ACCOUNT: '/verify_bank_account.php',
  GET_STATES: '/get-locations.php?action=states',
  GET_LGAS: '/get-locations.php?action=lgas',
};

// ============================================
// LOGGER
// ============================================

const logger = {
  info: (message, data) => console.log(`[SIGNUP] ℹ️ ${message}`, data || ''),
  error: (message, error) => console.error(`[SIGNUP] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[SIGNUP] 🐛 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[SIGNUP] ⚠️ ${message}`, data || ''),
  success: (message, data) => console.log(`[SIGNUP] ✅ ${message}`, data || ''),
};

// ============================================
// CONSTANTS
// ============================================

const USER_TYPES = {
  SUPPLIER: 'supplier',
  BUYER: 'buyer',
};

// Fallback banks in case API fails
const FALLBACK_BANKS = [
  { code: '001', name: 'Access Bank' },
  { code: '002', name: 'Citibank' },
  { code: '003', name: 'Ecobank' },
  { code: '004', name: 'Fidelity Bank' },
  { code: '005', name: 'First Bank of Nigeria' },
  { code: '006', name: 'First City Monument Bank' },
  { code: '007', name: 'Guaranty Trust Bank' },
  { code: '008', name: 'Heritage Bank' },
  { code: '009', name: 'Keystone Bank' },
  { code: '010', name: 'Polaris Bank' },
  { code: '011', name: 'Providus Bank' },
  { code: '012', name: 'Stanbic IBTC Bank' },
  { code: '013', name: 'Standard Chartered Bank' },
  { code: '014', name: 'Sterling Bank' },
  { code: '015', name: 'Suntrust Bank' },
  { code: '016', name: 'Union Bank of Nigeria' },
  { code: '017', name: 'United Bank for Africa' },
  { code: '018', name: 'Unity Bank' },
  { code: '019', name: 'Wema Bank' },
  { code: '020', name: 'Zenith Bank' },
  { code: '021', name: 'Kuda Bank' },
  { code: '022', name: 'Palmpay' },
  { code: '023', name: 'OPAY' },
  { code: '024', name: 'Moniepoint' },
];

// ============================================
// VALIDATION SCHEMAS
// ============================================

class ValidationError extends Error {
  constructor(message, field = null) {
    super(message);
    this.field = field;
    this.name = 'ValidationError';
  }
}

const validators = {
  required: (value, message = 'This field is required') => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      throw new ValidationError(message);
    }
  },
  email: (value, message = 'Please enter a valid email address') => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      throw new ValidationError(message);
    }
  },
  phone: (value, message = 'Please enter a valid phone number') => {
    const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,5}[-\s\.]?[0-9]{1,5}$/;
    if (!phoneRegex.test(value) && value.length < 8) {
      throw new ValidationError(message);
    }
  },
  password: (value) => {
    if (value.length < 8) {
      throw new ValidationError('Password must be at least 8 characters');
    }
    if (!/[A-Z]/.test(value)) {
      throw new ValidationError('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(value)) {
      throw new ValidationError('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(value)) {
      throw new ValidationError('Password must contain at least one number');
    }
  },
  passwordMatch: (password, confirmPassword) => {
    if (password !== confirmPassword) {
      throw new ValidationError('Passwords do not match');
    }
  },
  rcNumber: (value) => {
    const rcRegex = /^RC\d{6,}$/i;
    if (!rcRegex.test(value)) {
      throw new ValidationError('RC number must be in format RC123456');
    }
  },
  bankAccountNumber: (value) => {
    const accountRegex = /^[0-9]{10}$/;
    if (!accountRegex.test(value)) {
      throw new ValidationError('Bank account number must be 10 digits');
    }
  },
};

const validateField = (field, value, validations = []) => {
  const errors = [];
  for (const validation of validations) {
    try {
      validation(value);
    } catch (error) {
      if (error instanceof ValidationError) {
        errors.push(error.message);
      } else {
        errors.push('Validation error');
      }
    }
  }
  return errors;
};

// ============================================
// BANK VERIFICATION SERVICE
// ============================================

const bankVerificationService = {
  async getBanks() {
    try {
      logger.debug('Fetching banks from API');
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.GET_BANKS}`,
        {
          headers: {
            'Accept': 'application/json',
          },
          timeout: 15000,
        }
      );

      if (response.data.success && response.data.data.length > 0) {
        logger.success('Banks fetched successfully', { count: response.data.data.length });
        return { success: true, banks: response.data.data };
      }

      logger.warn('No banks returned from API, using fallback');
      return { success: true, banks: FALLBACK_BANKS };
    } catch (error) {
      logger.error('Failed to fetch banks', error);
      return { success: true, banks: FALLBACK_BANKS };
    }
  },

  async verifyAccount(accountNumber, bankCode) {
    try {
      logger.debug('Verifying bank account', { accountNumber, bankCode });
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.VERIFY_BANK_ACCOUNT}`,
        {
          params: {
            account_number: accountNumber,
            bank_code: bankCode,
          },
          headers: {
            'Accept': 'application/json',
          },
          timeout: 15000,
        }
      );

      if (response.data.success) {
        logger.success('Account verified successfully', {
          accountName: response.data.data.account_name,
        });
        return {
          success: true,
          data: {
            accountNumber: response.data.data.account_number,
            accountName: response.data.data.account_name,
            bankCode: response.data.data.bank_code,
          },
        };
      }

      const errorMessage = response.data.message || 'Account verification failed';
      logger.error('Account verification failed', { message: errorMessage });
      return { success: false, message: errorMessage };
    } catch (error) {
      logger.error('Account verification error', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to verify account. Please check the account number.',
      };
    }
  },
};

// ============================================
// LOCATION SERVICE
// ============================================

const locationService = {
  async getStates() {
    try {
      logger.debug('Fetching states from API');
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.GET_STATES}`,
        {
          headers: {
            'Accept': 'application/json',
          },
          timeout: 15000,
        }
      );

      if (response.data.success && response.data.data.length > 0) {
        logger.success('States fetched successfully', { count: response.data.data.length });
        return { success: true, data: response.data.data };
      }

      logger.warn('No states returned from API');
      return { success: false, message: 'No states found' };
    } catch (error) {
      logger.error('Failed to fetch states', error);
      return { success: false, message: 'Failed to load states' };
    }
  },

  async getLgas(stateId) {
    try {
      logger.debug('Fetching LGAs for state:', stateId);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.GET_LGAS}&state_id=${stateId}`,
        {
          headers: {
            'Accept': 'application/json',
          },
          timeout: 15000,
        }
      );

      if (response.data.success && response.data.data.length > 0) {
        logger.success('LGAs fetched successfully', { count: response.data.data.length });
        return { success: true, data: response.data.data };
      }

      logger.warn('No LGAs returned for state:', stateId);
      return { success: true, data: [] };
    } catch (error) {
      logger.error('Failed to fetch LGAs', error);
      return { success: false, message: 'Failed to load local governments' };
    }
  },
};

// ============================================
// SIGNUP SERVICE WITH API INTEGRATION
// ============================================

const signupService = {
  async registerSupplier(data) {
    try {
      logger.info('Registering supplier', { companyName: data.companyName, email: data.email });
      
      const payload = {
        email: data.email,
        password: data.password,
        phone: data.phone,
        companyName: data.companyName,
        rcNumber: data.rcNumber,
        stateId: data.stateId,
        lgaId: data.lgaId,
        streetAddress: data.streetAddress,
        city: data.city,
        bankName: data.bankName || '',
        bankCode: data.bankCode || '',
        bankAccountNumber: data.bankAccountNumber || '',
        bankAccountName: data.bankAccountName || '',
      };

      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.REGISTER_SUPPLIER}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          timeout: 30000,
        }
      );

      logger.success('Supplier registration successful', { 
        email: data.email,
        response: response.data 
      });
      
      return { 
        success: true, 
        data: response.data,
        message: response.data.message || 'Registration successful. Please verify your email.'
      };
    } catch (error) {
      logger.error('Supplier registration failed', error);
      return { 
        success: false, 
        error: this._handleError(error) 
      };
    }
  },

  async registerBuyer(data) {
    try {
      logger.info('Registering buyer', { name: data.name, email: data.email });
      
      const payload = {
        email: data.email,
        password: data.password,
        phone: data.phone,
        name: data.name,
        organisation: data.organisation,
        stateId: data.stateId,
        lgaId: data.lgaId,
        streetAddress: data.streetAddress,
        city: data.city,
      };

      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.REGISTER_BUYER}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          timeout: 30000,
        }
      );

      logger.success('Buyer registration successful', { 
        email: data.email,
        response: response.data 
      });
      
      return { 
        success: true, 
        data: response.data,
        message: response.data.message || 'Registration successful. Please verify your email.'
      };
    } catch (error) {
      logger.error('Buyer registration failed', error);
      return { 
        success: false, 
        error: this._handleError(error) 
      };
    }
  },

  async resendVerification(email) {
    try {
      logger.info('Resending verification email', { email });
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.RESEND_VERIFICATION}`,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          timeout: 30000,
        }
      );
      logger.success('Verification email resent', response.data);
      return { success: true, message: response.data.message };
    } catch (error) {
      logger.error('Resend verification failed', error);
      return { 
        success: false, 
        error: this._handleError(error) 
      };
    }
  },

  async checkVerificationStatus(email) {
    try {
      logger.debug('Checking verification status for:', email);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CHECK_VERIFICATION}`,
        { 
          params: { email },
          headers: {
            'Accept': 'application/json',
          },
          timeout: 15000,
        }
      );
      return { 
        success: true, 
        isVerified: response.data.is_verified === 1,
        data: response.data 
      };
    } catch (error) {
      logger.error('Check verification failed', error);
      return { success: false, isVerified: false, error: error.message };
    }
  },

  _handleError(error) {
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        return 'Request timeout. Please check your connection and try again.';
      }
      return 'Network error. Please check your internet connection.';
    }

    const { status, data } = error.response;
    
    if (status === 400 && data.errors) {
      const errorMessages = Object.values(data.errors).flat().join(', ');
      return errorMessages || 'Validation failed. Please check your input.';
    }

    if (status === 404) {
      return 'API endpoint not found. Please check your configuration.';
    }

    if (status === 409) {
      return data.message || 'An account with this email or RC number already exists.';
    }

    if (status >= 500) {
      return 'Server error. Please try again later.';
    }

    return data.message || data.error || 'Registration failed. Please try again.';
  },
};

// ============================================
// BANK ACCOUNT VERIFICATION FIELD COMPONENT
// ============================================

const BankAccountVerificationField = React.memo(({
  value,
  bankCode,
  bankName,
  accountName,
  isVerified,
  isVerifying,
  banks,
  onAccountChange,
  onBankChange,
  onVerificationComplete,
  error,
  helperText,
  disabled,
  required,
  ...props
}) => {
  const [localError, setLocalError] = useState('');
  const [localHelperText, setLocalHelperText] = useState('');
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const verificationTimeoutRef = useRef(null);
  const lastVerificationRef = useRef({ accountNumber: '', bankCode: '' });

  useEffect(() => {
    setVerificationAttempted(false);
    setLocalError('');
  }, [value, bankCode]);

  useEffect(() => {
    if (verificationTimeoutRef.current) {
      clearTimeout(verificationTimeoutRef.current);
    }

    const shouldVerify = 
      value?.length === 10 && 
      bankCode && 
      !isVerified && 
      !isVerifying &&
      !verificationAttempted;

    if (shouldVerify) {
      const isDifferent = 
        lastVerificationRef.current.accountNumber !== value ||
        lastVerificationRef.current.bankCode !== bankCode;

      if (isDifferent) {
        lastVerificationRef.current = { accountNumber: value, bankCode };
        
        verificationTimeoutRef.current = setTimeout(() => {
          setVerificationAttempted(true);
          onVerificationComplete();
        }, 800);
      }
    }

    return () => {
      if (verificationTimeoutRef.current) {
        clearTimeout(verificationTimeoutRef.current);
      }
    };
  }, [value, bankCode, isVerified, isVerifying, verificationAttempted, onVerificationComplete]);

  const handleAccountChange = (e) => {
    const rawValue = e.target.value;
    const sanitized = rawValue.replace(/\D/g, '').slice(0, 10);
    
    setVerificationAttempted(false);
    setLocalError('');
    lastVerificationRef.current = { accountNumber: '', bankCode: '' };
    
    onAccountChange(sanitized);
    setLocalHelperText('');
  };

  const handleBankChange = (e) => {
    const selectedBank = banks.find(b => b.code === e.target.value);
    setVerificationAttempted(false);
    setLocalError('');
    lastVerificationRef.current = { accountNumber: '', bankCode: '' };
    
    onBankChange(e.target.value, selectedBank?.name || '');
    setLocalHelperText('');
  };

  const getHelperText = () => {
    if (localError) return localError;
    if (helperText) return helperText;
    if (value?.length === 10 && !isVerified && !isVerifying && verificationAttempted) {
      return '⚠️ Account not verified. Please check the account number or select a different bank.';
    }
    if (value?.length === 10 && !isVerified && !isVerifying && !verificationAttempted) {
      return 'Verifying account...';
    }
    if (isVerifying) return 'Verifying account...';
    if (isVerified) return '✓ Account verified successfully';
    if (value?.length > 0 && value?.length < 10) {
      return `Enter ${10 - value.length} more digit(s)`;
    }
    return 'Enter 10-digit account number';
  };

  const isError = () => {
    if (error) return true;
    if (localError) return true;
    if (value?.length === 10 && !isVerified && !isVerifying && verificationAttempted) return true;
    return false;
  };

  const getEndAdornment = () => {
    if (isVerifying) {
      return <CircularProgress size={20} />;
    }
    if (isVerified) {
      return <VerifiedOutlined sx={{ color: '#4caf50' }} />;
    }
    if (value?.length === 10 && !isVerified && verificationAttempted) {
      return <ErrorIcon sx={{ color: '#f44336' }} />;
    }
    if (value?.length === 10 && !isVerified && !verificationAttempted) {
      return <CircularProgress size={20} />;
    }
    return <AccountBalanceIcon sx={{ color: BRAND_COLORS.textSecondary }} />;
  };

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={12} md={5}>
        <FormControl fullWidth error={isError()} disabled={disabled}>
          <InputLabel>Select Bank</InputLabel>
          <Select
            value={bankCode || ''}
            label="Select Bank"
            onChange={handleBankChange}
          >
            <MenuItem value="">Select Bank</MenuItem>
            {banks.map((bank) => (
              <MenuItem key={bank.code} value={bank.code}>
                {bank.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{!bankCode && 'Please select your bank'}</FormHelperText>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={7}>
        <TextField
          fullWidth
          label="Account Number"
          value={value}
          onChange={handleAccountChange}
          error={isError()}
          helperText={getHelperText()}
          disabled={disabled || isVerifying}
          placeholder="Enter 10-digit account number"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CreditCardOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {getEndAdornment()}
              </InputAdornment>
            ),
          }}
          {...props}
        />
      </Grid>
      {isVerified && accountName && (
        <Grid item xs={12}>
          <Alert
            severity="success"
            icon={<VerifiedOutlined />}
            sx={{
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Account Holder:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND_COLORS.primary }}>
                {accountName}
              </Typography>
            </Box>
          </Alert>
        </Grid>
      )}
      {!isVerified && verificationAttempted && value?.length === 10 && bankCode && (
        <Grid item xs={12}>
          <Alert
            severity="error"
            icon={<ErrorIcon />}
            sx={{ borderRadius: 2 }}
          >
            <Typography variant="body2">
              Account verification failed. Please verify that the account number is correct or try a different bank.
            </Typography>
          </Alert>
        </Grid>
      )}
    </Grid>
  );
});

BankAccountVerificationField.displayName = 'BankAccountVerificationField';

// ============================================
// VERIFICATION PENDING COMPONENT
// ============================================

const VerificationPending = React.memo(({ 
  email, 
  userType, 
  onResend, 
  onLogin, 
  isResending,
  resendMessage,
  resendError 
}) => {
  return (
    <Box sx={{ textAlign: 'center', py: { xs: 2, sm: 4 } }}>
      <Avatar
        sx={{
          width: { xs: 70, sm: 90 },
          height: { xs: 70, sm: 90 },
          mx: 'auto',
          mb: 3,
          background: `linear-gradient(135deg, ${BRAND_COLORS.accent}, ${BRAND_COLORS.primary})`,
          boxShadow: `0 8px 32px ${alpha(BRAND_COLORS.primary, 0.3)}`,
        }}
      >
        <MarkEmailReadOutlined sx={{ fontSize: { xs: 36, sm: 48 }, color: '#FFFFFF' }} />
      </Avatar>
      
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 700, 
          mb: 1, 
          color: BRAND_COLORS.text,
          fontSize: { xs: '1.5rem', sm: '2rem' }
        }}
      >
        Verify Your Email
      </Typography>
      
      <Typography 
        variant="body1" 
        sx={{ 
          color: BRAND_COLORS.textSecondary, 
          mb: 1,
          fontSize: { xs: '0.9rem', sm: '1rem' }
        }}
      >
        We've sent a verification link to
      </Typography>
      
      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 600, 
          color: BRAND_COLORS.primary, 
          mb: 3,
          wordBreak: 'break-all',
          fontSize: { xs: '1rem', sm: '1.25rem' }
        }}
      >
        {email}
      </Typography>
      
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: BRAND_COLORS.primaryPale,
          borderRadius: 2,
          mb: 3,
          border: `1px solid ${alpha(BRAND_COLORS.primary, 0.1)}`,
          textAlign: 'left',
        }}
      >
        <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary }}>
          <strong style={{ color: BRAND_COLORS.primary }}>📧 Please check your email</strong>
          <br />
          Click the verification link in your email to activate your account.
          The link will expire in 24 hours.
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            color: BRAND_COLORS.textSecondary, 
            mt: 1, 
            display: 'block',
            opacity: 0.7,
            fontSize: '0.7rem'
          }}
        >
          <strong>Note:</strong> If you don't see the email, please check your spam folder.
        </Typography>
      </Paper>

      {(resendMessage || resendError) && (
        <Alert 
          severity={resendError ? 'error' : 'success'} 
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            textAlign: 'left',
          }}
          onClose={() => {}}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            {resendError ? 'Failed to Resend' : 'Email Resent'}
          </AlertTitle>
          {resendMessage || resendError}
        </Alert>
      )}
      
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        justifyContent="center"
        sx={{ width: '100%' }}
      >
        <Button
          variant="outlined"
          onClick={onResend}
          disabled={isResending}
          startIcon={isResending ? <CircularProgress size={20} /> : <RefreshOutlined />}
          sx={{
            color: BRAND_COLORS.primary,
            borderColor: BRAND_COLORS.primary,
            borderRadius: 2,
            py: 1.2,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: BRAND_COLORS.primaryDark,
              background: BRAND_COLORS.primaryPale,
            },
            '&.Mui-disabled': {
              borderColor: BRAND_COLORS.border,
              color: BRAND_COLORS.textSecondary,
            },
            flex: { xs: '1', sm: 'none' },
          }}
        >
          {isResending ? 'Sending...' : 'Resend Email'}
        </Button>
        
        <Button
          onClick={onLogin}
          variant="contained"
          sx={{
            borderRadius: 2,
            py: 1.2,
            px: 4,
            background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryDark})`,
            color: BRAND_COLORS.surface,
            fontWeight: 600,
            textTransform: 'none',
            transition: 'all 0.3s ease',
            boxShadow: `0 2px 12px ${alpha(BRAND_COLORS.primary, 0.3)}`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 20px ${alpha(BRAND_COLORS.primary, 0.4)}`,
            },
            flex: { xs: '1', sm: 'none' },
          }}
        >
          Go to Login
        </Button>
      </Stack>
    </Box>
  );
});

VerificationPending.displayName = 'VerificationPending';

// ============================================
// ANIMATED STEPPER COMPONENT
// ============================================

const AnimatedStepContent = React.memo(({ children, active }) => {
  return (
    <Collapse in={active} timeout={400}>
      <Box
        sx={{
          pt: 2,
          pb: 1,
          animation: 'fadeInUp 0.5s ease-out',
        }}
      >
        {children}
      </Box>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Collapse>
  );
});

AnimatedStepContent.displayName = 'AnimatedStepContent';

// ============================================
// STEPPER STEPS
// ============================================

const getSteps = (userType) => {
  const steps = [
    { label: 'Account Type' },
    { label: 'Personal Details' },
    { label: 'Account Credentials' },
  ];

  if (userType === USER_TYPES.SUPPLIER) {
    steps.splice(1, 0, { label: 'Business Details' });
  }

  return steps;
};

// ============================================
// MAIN SIGNUP COMPONENT
// ============================================

const SignupPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // ============================================
  // STATE DECLARATIONS
  // ============================================

  const [activeStep, setActiveStep] = useState(0);
  const [userType, setUserType] = useState(USER_TYPES.SUPPLIER);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  
  const [banks, setBanks] = useState(FALLBACK_BANKS);
  const [banksLoading, setBanksLoading] = useState(false);
  const [isVerifyingAccount, setIsVerifyingAccount] = useState(false);
  const [accountVerificationError, setAccountVerificationError] = useState('');
  
  const [locations, setLocations] = useState({
    states: [],
    lgas: [],
    loading: false,
    error: null,
  });

  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [registrationData, setRegistrationData] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    userType: USER_TYPES.SUPPLIER,
    companyName: '',
    rcNumber: '',
    officeAddress: '',
    bankName: '',
    bankCode: '',
    bankAccountNumber: '',
    bankAccountName: '',
    isBankVerified: false,
    name: '',
    organisation: '',
    stateId: '',
    lgaId: '',
    streetAddress: '',
    city: '',
    postalCode: '',
  });

  const [errors, setErrors] = useState({});

  const formRef = useRef(null);
  const verificationCheckInterval = useRef(null);
  const bankFetchAttempted = useRef(false);
  const locationFetchAttempted = useRef(false);

  const steps = useMemo(() => getSteps(userType), [userType]);

  // ============================================
  // EFFECT HOOKS
  // ============================================

  useEffect(() => {
    const fetchBanks = async () => {
      if (bankFetchAttempted.current) return;
      bankFetchAttempted.current = true;
      
      setBanksLoading(true);
      try {
        const result = await bankVerificationService.getBanks();
        if (result.success && result.banks.length > 0) {
          setBanks(result.banks);
          logger.success('Banks loaded', { count: result.banks.length });
        } else {
          setBanks(FALLBACK_BANKS);
          logger.warn('Using fallback banks');
        }
      } catch (error) {
        logger.error('Error fetching banks, using fallback', error);
        setBanks(FALLBACK_BANKS);
      } finally {
        setBanksLoading(false);
      }
    };

    fetchBanks();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      if (locationFetchAttempted.current) return;
      locationFetchAttempted.current = true;
      
      setLocations(prev => ({ ...prev, loading: true, error: null }));
      try {
        const result = await locationService.getStates();
        if (result.success) {
          setLocations(prev => ({ ...prev, states: result.data }));
          logger.success('States loaded', { count: result.data.length });
        } else {
          setLocations(prev => ({ ...prev, error: result.message }));
          logger.error('Failed to load states', result.message);
        }
      } catch (error) {
        logger.error('Error fetching states', error);
        setLocations(prev => ({ ...prev, error: 'Failed to load states' }));
      } finally {
        setLocations(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStates();
  }, []);

  useEffect(() => {
    if (formData.stateId) {
      const fetchLgas = async () => {
        setLocations(prev => ({ ...prev, loading: true }));
        try {
          const result = await locationService.getLgas(formData.stateId);
          if (result.success) {
            setLocations(prev => ({ ...prev, lgas: result.data }));
            logger.success('LGAs loaded', { count: result.data.length });
          } else {
            setLocations(prev => ({ ...prev, lgas: [], error: result.message }));
          }
        } catch (error) {
          logger.error('Error fetching LGAs', error);
          setLocations(prev => ({ ...prev, lgas: [] }));
        } finally {
          setLocations(prev => ({ ...prev, loading: false }));
        }
      };
      fetchLgas();
    } else {
      setLocations(prev => ({ ...prev, lgas: [] }));
    }
  }, [formData.stateId]);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        logger.info('User already authenticated, redirecting to dashboard');
        navigate('/WaitersDashboard', { replace: true });
        return;
      }
      setIsPageLoading(false);
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const storedData = localStorage.getItem('pending_verification');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        setRegistrationData(data);
        setFormData(prev => ({
          ...prev,
          email: data.email || '',
          userType: data.userType || USER_TYPES.SUPPLIER,
        }));
        setSignupComplete(true);
        logger.info('Found pending verification data', { email: data.email });
      } catch (e) {
        localStorage.removeItem('pending_verification');
      }
    }
  }, []);

  useEffect(() => {
    if (signupComplete && registrationData?.email) {
      let checkCount = 0;
      const maxChecks = 30;
      
      verificationCheckInterval.current = setInterval(async () => {
        checkCount++;
        logger.debug(`Checking verification (${checkCount}/${maxChecks})`, { email: registrationData.email });
        
        const result = await signupService.checkVerificationStatus(registrationData.email);
        if (result.success && result.isVerified) {
          clearInterval(verificationCheckInterval.current);
          logger.success('Email verified! Redirecting to login...');
          localStorage.removeItem('pending_verification');
          navigate('/RawAuth', {
            state: {
              message: 'Your email has been verified! Please login to continue.',
              email: registrationData.email,
              userType: registrationData.userType,
            }
          });
        } else if (checkCount >= maxChecks) {
          clearInterval(verificationCheckInterval.current);
          logger.info('Verification polling stopped after max attempts');
        }
      }, 10000);

      return () => {
        if (verificationCheckInterval.current) {
          clearInterval(verificationCheckInterval.current);
        }
      };
    }
  }, [signupComplete, registrationData, navigate]);

  useEffect(() => {
    if (!isPageLoading && !signupComplete) {
      const firstInput = formRef.current?.querySelector('input:not([disabled])');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 300);
      }
    }
  }, [isPageLoading, activeStep, signupComplete]);

  // ============================================
  // GET STEP FIELDS
  // ============================================

  const getStepFields = useCallback((step) => {
    const fields = {
      0: ['userType'],
    };
    
    if (userType === USER_TYPES.SUPPLIER) {
      fields[1] = ['companyName', 'rcNumber', 'stateId', 'lgaId', 'streetAddress', 'city', 'phone'];
      fields[2] = ['name', 'organisation', 'stateId', 'lgaId', 'streetAddress', 'city', 'phone'];
    } else {
      fields[1] = ['name', 'organisation', 'stateId', 'lgaId', 'streetAddress', 'city', 'phone'];
    }
    
    const lastStep = steps.length - 1;
    fields[lastStep] = ['email', 'password', 'confirmPassword'];
    
    return fields[step] || [];
  }, [userType, steps.length]);

  // ============================================
  // VALIDATE STEP
  // ============================================

  const validateStep = useCallback((step) => {
    const fields = getStepFields(step);
    const newErrors = {};
    let isValid = true;

    for (const field of fields) {
      const value = formData[field];
      const fieldValidations = [];

      if (field === 'email') {
        fieldValidations.push((v) => validators.required(v, 'Email is required'));
        fieldValidations.push((v) => validators.email(v));
      } else if (field === 'password') {
        fieldValidations.push((v) => validators.required(v, 'Password is required'));
        fieldValidations.push((v) => validators.password(v));
      } else if (field === 'confirmPassword') {
        fieldValidations.push((v) => validators.required(v, 'Please confirm your password'));
        fieldValidations.push((v) => validators.passwordMatch(formData.password, v));
      } else if (field === 'phone') {
        fieldValidations.push((v) => validators.required(v, 'Phone number is required'));
        fieldValidations.push((v) => validators.phone(v));
      } else if (field === 'companyName') {
        fieldValidations.push((v) => validators.required(v, 'Company name is required'));
      } else if (field === 'rcNumber') {
        fieldValidations.push((v) => validators.required(v, 'RC number is required'));
        fieldValidations.push((v) => validators.rcNumber(v));
      } else if (field === 'stateId') {
        fieldValidations.push((v) => validators.required(v, 'Please select your state'));
      } else if (field === 'lgaId') {
        fieldValidations.push((v) => validators.required(v, 'Please select your local government'));
      } else if (field === 'streetAddress') {
        fieldValidations.push((v) => validators.required(v, 'Street address is required'));
      } else if (field === 'city') {
        fieldValidations.push((v) => validators.required(v, 'City/Town is required'));
      } else if (field === 'name') {
        fieldValidations.push((v) => validators.required(v, 'Full name is required'));
      } else if (field === 'organisation') {
        fieldValidations.push((v) => validators.required(v, 'Organisation name is required'));
      } else if (field === 'bankAccountNumber') {
        if (value && value.trim()) {
          try {
            validators.bankAccountNumber(value);
            if (value.length === 10 && !formData.isBankVerified) {
              fieldValidations.push(() => {
                throw new ValidationError('Please verify your bank account');
              });
            }
          } catch (error) {
            if (error instanceof ValidationError) {
              fieldValidations.push(() => { throw error; });
            }
          }
        }
      }

      const fieldErrors = validateField(field, value, fieldValidations);
      if (fieldErrors.length > 0) {
        newErrors[field] = fieldErrors[0];
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  }, [formData, userType, getStepFields]);

  // ============================================
  // HANDLE FORM DATA CHANGE
  // ============================================

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    if (serverError) {
      setServerError('');
    }
  }, [errors, serverError]);

  const handleStateChange = useCallback((e) => {
    const stateId = e.target.value;
    setFormData(prev => ({
      ...prev,
      stateId,
      lgaId: '',
    }));
    if (errors.lgaId) {
      setErrors(prev => ({ ...prev, lgaId: '' }));
    }
    if (errors.stateId) {
      setErrors(prev => ({ ...prev, stateId: '' }));
    }
  }, [errors]);

  const handleLgaChange = useCallback((e) => {
    setFormData(prev => ({
      ...prev,
      lgaId: e.target.value,
    }));
    if (errors.lgaId) {
      setErrors(prev => ({ ...prev, lgaId: '' }));
    }
  }, [errors]);

  const handleBankAccountChange = useCallback((accountNumber) => {
    setFormData((prev) => ({
      ...prev,
      bankAccountNumber: accountNumber,
      isBankVerified: false,
      bankAccountName: '',
    }));
    
    setAccountVerificationError('');
    if (errors.bankAccountNumber) {
      setErrors((prev) => ({
        ...prev,
        bankAccountNumber: '',
      }));
    }
  }, [errors]);

  const handleBankChange = useCallback((bankCode, bankName) => {
    setFormData((prev) => ({
      ...prev,
      bankCode,
      bankName,
      isBankVerified: false,
      bankAccountName: '',
    }));
    
    setAccountVerificationError('');
    if (errors.bankCode) {
      setErrors((prev) => ({
        ...prev,
        bankCode: '',
      }));
    }
  }, [errors]);

  const handleVerifyAccount = useCallback(async () => {
    const { bankAccountNumber, bankCode } = formData;
    
    if (!bankAccountNumber || bankAccountNumber.length < 10) {
      setAccountVerificationError('Please enter a valid 10-digit account number');
      return;
    }

    if (!bankCode) {
      setAccountVerificationError('Please select your bank');
      return;
    }

    setIsVerifyingAccount(true);
    setAccountVerificationError('');

    try {
      const result = await bankVerificationService.verifyAccount(bankAccountNumber, bankCode);
      
      if (result.success) {
        setFormData((prev) => ({
          ...prev,
          bankAccountName: result.data.accountName,
          isBankVerified: true,
        }));
        setAccountVerificationError('');
        logger.success('Account verified', { accountName: result.data.accountName });
      } else {
        setFormData((prev) => ({
          ...prev,
          isBankVerified: false,
          bankAccountName: '',
        }));
        setAccountVerificationError(result.message || 'Account verification failed');
        logger.error('Account verification failed', result.message);
      }
    } catch (error) {
      setFormData((prev) => ({
        ...prev,
        isBankVerified: false,
        bankAccountName: '',
      }));
      setAccountVerificationError('Failed to verify account. Please try again.');
      logger.error('Verification error', error);
    } finally {
      setIsVerifyingAccount(false);
    }
  }, [formData.bankAccountNumber, formData.bankCode]);

  const handleUserTypeChange = useCallback((e) => {
    const newType = e.target.value;
    setUserType(newType);
    setFormData((prev) => ({
      ...prev,
      userType: newType,
    }));
    setActiveStep(0);
    setErrors({});
    logger.debug('User type changed', { newType });
  }, []);

  const handleResendVerification = useCallback(async () => {
    if (!registrationData?.email) {
      setResendError('No email address found');
      return;
    }

    setIsResending(true);
    setResendMessage('');
    setResendError('');

    try {
      const result = await signupService.resendVerification(registrationData.email);
      if (result.success) {
        setResendMessage('Verification email resent successfully! Please check your inbox and spam folder.');
        logger.success('Verification email resent', { email: registrationData.email });
      } else {
        setResendError(result.error || 'Failed to resend verification email.');
        logger.error('Failed to resend verification', result.error);
      }
    } catch (error) {
      setResendError('An error occurred. Please try again.');
      logger.error('Resend verification error', error);
    } finally {
      setIsResending(false);
    }
  }, [registrationData]);

  const handleGoToLogin = useCallback(() => {
    localStorage.removeItem('pending_verification');
    navigate('/RawAuth', {
      state: {
        message: 'Please login to your account.',
        email: registrationData?.email,
        userType: registrationData?.userType,
      }
    });
  }, [navigate, registrationData]);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    setServerError('');
    setSuccessMessage('');

    try {
      const submitData = { ...formData };
      delete submitData.confirmPassword;
      delete submitData.userType;
      delete submitData.isBankVerified;
      delete submitData.officeAddress;

      logger.debug('Submitting data:', submitData);

      let result;
      if (userType === USER_TYPES.SUPPLIER) {
        result = await signupService.registerSupplier(submitData);
      } else {
        result = await signupService.registerBuyer(submitData);
      }

      logger.debug('Registration result:', result);

      if (result.success) {
        const email = formData.email;
        const userTypeValue = userType;
        const userId = result.data.data?.user_id || null;
        
        const pendingData = {
          email: email,
          userType: userTypeValue,
          user_id: userId,
          registeredAt: new Date().toISOString(),
        };
        localStorage.setItem('pending_verification', JSON.stringify(pendingData));
        setRegistrationData(pendingData);
        
        setSuccessMessage(result.message || 'Registration successful! Please verify your email.');
        logger.success('Registration successful', { userType, email: formData.email });
        
        setSignupComplete(true);
      } else {
        setServerError(result.error || 'Registration failed. Please try again.');
        logger.error('Registration failed', result.error);
        
        if (formRef.current) {
          formRef.current.scrollTop = 0;
        }
      }
    } catch (error) {
      logger.error('Registration error', error);
      setServerError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [formData, userType]);

  const handleNext = useCallback(() => {
    if (!validateStep(activeStep)) {
      logger.debug('Step validation failed', { step: activeStep });
      return;
    }

    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
      logger.debug('Step advanced', { from: activeStep, to: activeStep + 1 });
      const contentArea = formRef.current?.querySelector('.MuiBox-root');
      if (contentArea) {
        contentArea.scrollTop = 0;
      }
    }
  }, [activeStep, steps.length, validateStep, handleSubmit]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
    setErrors({});
  }, []);

  // ============================================
  // HANDLE KEY PRESS (Enter key)
  // ============================================

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNext();
    }
  }, [handleNext]);

  // ============================================
  // COMMON TEXTFIELD STYLES
  // ============================================

  const getCommonSx = useCallback(() => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: BRAND_COLORS.surface,
      transition: 'all 0.3s ease',
      '& fieldset': {
        borderColor: BRAND_COLORS.border,
        borderWidth: 1.5,
      },
      '&:hover': {
        '& fieldset': {
          borderColor: BRAND_COLORS.primary,
        },
      },
      '&.Mui-focused': {
        '& fieldset': {
          borderColor: BRAND_COLORS.primary,
          borderWidth: 2,
        },
      },
      '& input': {
        color: BRAND_COLORS.text,
        '&::placeholder': {
          color: '#B0B0B0',
          opacity: 1,
        },
      },
      '& .MuiSelect-select': {
        color: BRAND_COLORS.text,
      },
    },
    '& .MuiInputLabel-root': {
      color: BRAND_COLORS.textSecondary,
      '&.Mui-focused': {
        color: BRAND_COLORS.primary,
      },
    },
    '& .MuiSelect-icon': {
      color: BRAND_COLORS.textSecondary,
    },
    '& .MuiFormHelperText-root': {
      color: BRAND_COLORS.textSecondary,
    },
  }), []);

  // ============================================
  // RENDER STEP CONTENT
  // ============================================

  const renderStepContent = useCallback((step) => {
    const commonSx = getCommonSx();

    // CASE 0: Account Type (always step 0)
    if (step === 0) {
      return (
        <Box>
          <Typography variant="h6" sx={{ color: BRAND_COLORS.text, mb: 1, fontWeight: 700 }}>
            Choose Your Account Type
          </Typography>
          <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary, mb: 3 }}>
            Select the role that best describes how you'll use the platform.
          </Typography>

          <RadioGroup value={userType} onChange={handleUserTypeChange} sx={{ gap: 2 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                background: userType === USER_TYPES.SUPPLIER ? BRAND_COLORS.primaryPale : BRAND_COLORS.background,
                border: `2px solid ${userType === USER_TYPES.SUPPLIER ? BRAND_COLORS.primary : BRAND_COLORS.border}`,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onClick={() => {
                setUserType(USER_TYPES.SUPPLIER);
                setFormData(prev => ({ ...prev, userType: USER_TYPES.SUPPLIER }));
              }}
            >
              <FormControlLabel
                value={USER_TYPES.SUPPLIER}
                control={<Radio sx={{ color: '#D0D0D0', '&.Mui-checked': { color: BRAND_COLORS.primary } }} />}
                label={
                  <Box>
                    <Typography variant="h6" sx={{ color: BRAND_COLORS.text, fontWeight: 600, fontSize: '1rem' }}>
                      <BusinessOutlined sx={{ mr: 1, verticalAlign: 'middle', color: BRAND_COLORS.primary }} />
                      Chemical Suppliers
                    </Typography>
                    <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary, mt: 0.5 }}>
                      Register your company to list chemical products
                    </Typography>
                  </Box>
                }
                sx={{ width: '100%', m: 0, '& .MuiFormControlLabel-label': { width: '100%' } }}
              />
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                background: userType === USER_TYPES.BUYER ? BRAND_COLORS.primaryPale : BRAND_COLORS.background,
                border: `2px solid ${userType === USER_TYPES.BUYER ? BRAND_COLORS.primary : BRAND_COLORS.border}`,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onClick={() => {
                setUserType(USER_TYPES.BUYER);
                setFormData(prev => ({ ...prev, userType: USER_TYPES.BUYER }));
              }}
            >
              <FormControlLabel
                value={USER_TYPES.BUYER}
                control={<Radio sx={{ color: '#D0D0D0', '&.Mui-checked': { color: BRAND_COLORS.primary } }} />}
                label={
                  <Box>
                    <Typography variant="h6" sx={{ color: BRAND_COLORS.text, fontWeight: 600, fontSize: '1rem' }}>
                      <StorefrontOutlined sx={{ mr: 1, verticalAlign: 'middle', color: BRAND_COLORS.primary }} />
                      Chemical Users
                    </Typography>
                    <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary, mt: 0.5 }}>
                      Create a user account
                    </Typography>
                  </Box>
                }
                sx={{ width: '100%', m: 0, '& .MuiFormControlLabel-label': { width: '100%' } }}
              />
            </Paper>
          </RadioGroup>
        </Box>
      );
    }

    // CASE 1: Business Details (Supplier) OR Personal Details (Buyer)
    if (step === 1) {
      if (userType === USER_TYPES.SUPPLIER) {
        // Supplier Business Details
        return (
          <Box>
            <Typography variant="h6" sx={{ color: BRAND_COLORS.text, mb: 1, fontWeight: 700 }}>
              Company Information
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary, mb: 3 }}>
              Enter your company details for verification
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  error={!!errors.companyName}
                  helperText={errors.companyName}
                  placeholder="Enter company name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="RC Number"
                  name="rcNumber"
                  value={formData.rcNumber}
                  onChange={handleChange}
                  error={!!errors.rcNumber}
                  helperText={errors.rcNumber || 'e.g., RC123456'}
                  placeholder="Enter your RC number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.stateId} sx={{ minWidth: 150 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    name="stateId"
                    value={formData.stateId}
                    onChange={handleStateChange}
                    label="State"
                    disabled={locations.loading}
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    {locations.states.map((state) => (
                      <MenuItem key={state.id} value={state.id}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.stateId && (
                    <FormHelperText error>{errors.stateId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.lgaId} sx={{ minWidth: 150 }}>
                  <InputLabel>Local Government</InputLabel>
                  <Select
                    name="lgaId"
                    value={formData.lgaId}
                    onChange={handleLgaChange}
                    label="Local Government"
                    disabled={!formData.stateId || locations.loading}
                  >
                    <MenuItem value="">
                      <em>{formData.stateId ? 'Select LGA' : 'Select State First'}</em>
                    </MenuItem>
                    {locations.lgas.map((lga) => (
                      <MenuItem key={lga.id} value={lga.id}>
                        {lga.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.lgaId && (
                    <FormHelperText error>{errors.lgaId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  name="streetAddress"
                  value={formData.streetAddress}
                  onChange={handleChange}
                  error={!!errors.streetAddress}
                  helperText={errors.streetAddress}
                  placeholder="Enter street address (e.g., 123 Main Street)"
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignItems: 'flex-start', pt: 1.5 }}>
                        <HomeOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City/Town"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  error={!!errors.city}
                  helperText={errors.city}
                  placeholder="Enter city or town"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  placeholder="Enter company phone number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>
            </Grid>
          </Box>
        );
      } else {
        // Buyer Personal Details
        return (
          <Box>
            <Typography variant="h6" sx={{ color: BRAND_COLORS.text, mb: 1, fontWeight: 700 }}>
              Personal Information
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary, mb: 3 }}>
              Enter your personal and organisation details
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="Enter your full name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organisation"
                  name="organisation"
                  value={formData.organisation}
                  onChange={handleChange}
                  error={!!errors.organisation}
                  helperText={errors.organisation}
                  placeholder="Enter your organisation name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ApartmentOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.stateId} sx={{ minWidth: 150 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    name="stateId"
                    value={formData.stateId}
                    onChange={handleStateChange}
                    label="State"
                    disabled={locations.loading}
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    {locations.states.map((state) => (
                      <MenuItem key={state.id} value={state.id}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.stateId && (
                    <FormHelperText error>{errors.stateId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.lgaId} sx={{ minWidth: 150 }}>
                  <InputLabel>Local Government</InputLabel>
                  <Select
                    name="lgaId"
                    value={formData.lgaId}
                    onChange={handleLgaChange}
                    label="Local Government"
                    disabled={!formData.stateId || locations.loading}
                  >
                    <MenuItem value="">
                      <em>{formData.stateId ? 'Select LGA' : 'Select State First'}</em>
                    </MenuItem>
                    {locations.lgas.map((lga) => (
                      <MenuItem key={lga.id} value={lga.id}>
                        {lga.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.lgaId && (
                    <FormHelperText error>{errors.lgaId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  name="streetAddress"
                  value={formData.streetAddress}
                  onChange={handleChange}
                  error={!!errors.streetAddress}
                  helperText={errors.streetAddress}
                  placeholder="Enter street address"
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignItems: 'flex-start', pt: 1.5 }}>
                        <HomeOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City/Town"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  error={!!errors.city}
                  helperText={errors.city}
                  placeholder="Enter city or town"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  placeholder="Enter your phone number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>
            </Grid>
          </Box>
        );
      }
    }

    // CASE 2: Personal Details (Supplier) OR Account Credentials (Buyer)
    if (step === 2) {
      if (userType === USER_TYPES.SUPPLIER) {
        // Supplier Personal Details
        return (
          <Box>
            <Typography variant="h6" sx={{ color: BRAND_COLORS.text, mb: 1, fontWeight: 700 }}>
              Personal Information
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary, mb: 3 }}>
              Enter your personal details
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="Enter your full name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organisation"
                  name="organisation"
                  value={formData.organisation}
                  onChange={handleChange}
                  error={!!errors.organisation}
                  helperText={errors.organisation}
                  placeholder="Enter your organisation name"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ApartmentOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.stateId} sx={{ minWidth: 150 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    name="stateId"
                    value={formData.stateId}
                    onChange={handleStateChange}
                    label="State"
                    disabled={locations.loading}
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    {locations.states.map((state) => (
                      <MenuItem key={state.id} value={state.id}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.stateId && (
                    <FormHelperText error>{errors.stateId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.lgaId} sx={{ minWidth: 150 }}>
                  <InputLabel>Local Government</InputLabel>
                  <Select
                    name="lgaId"
                    value={formData.lgaId}
                    onChange={handleLgaChange}
                    label="Local Government"
                    disabled={!formData.stateId || locations.loading}
                  >
                    <MenuItem value="">
                      <em>{formData.stateId ? 'Select LGA' : 'Select State First'}</em>
                    </MenuItem>
                    {locations.lgas.map((lga) => (
                      <MenuItem key={lga.id} value={lga.id}>
                        {lga.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.lgaId && (
                    <FormHelperText error>{errors.lgaId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  name="streetAddress"
                  value={formData.streetAddress}
                  onChange={handleChange}
                  error={!!errors.streetAddress}
                  helperText={errors.streetAddress}
                  placeholder="Enter street address"
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignItems: 'flex-start', pt: 1.5 }}>
                        <HomeOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City/Town"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  error={!!errors.city}
                  helperText={errors.city}
                  placeholder="Enter city or town"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  placeholder="Enter your phone number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>
            </Grid>
          </Box>
        );
      } else {
        // Account Credentials (Buyer)
        return (
          <Box>
            <Typography variant="h6" sx={{ color: BRAND_COLORS.text, mb: 1, fontWeight: 700 }}>
              Account Credentials
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary, mb: 3 }}>
              Create your login credentials
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  placeholder="Enter your email address"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password || 'Min 8 chars with uppercase, lowercase, and number'}
                  placeholder="Create a strong password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={() => setShowPassword(!showPassword)} 
                          edge="end" 
                          sx={{ color: BRAND_COLORS.textSecondary }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  placeholder="Confirm your password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                          edge="end" 
                          sx={{ color: BRAND_COLORS.textSecondary }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={commonSx}
                />
              </Grid>
            </Grid>
          </Box>
        );
      }
    }

    // CASE 3: Account Credentials (Supplier only)
    if (step === 3 && userType === USER_TYPES.SUPPLIER) {
      return (
        <Box>
          <Typography variant="h6" sx={{ color: BRAND_COLORS.text, mb: 1, fontWeight: 700 }}>
            Account Credentials
          </Typography>
          <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary, mb: 3 }}>
            Create your login credentials
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                placeholder="Enter your email address"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={commonSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password || 'Min 8 chars with uppercase, lowercase, and number'}
                placeholder="Create a strong password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={() => setShowPassword(!showPassword)} 
                        edge="end" 
                        sx={{ color: BRAND_COLORS.textSecondary }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={commonSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                placeholder="Confirm your password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: BRAND_COLORS.textSecondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                        edge="end" 
                        sx={{ color: BRAND_COLORS.textSecondary }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={commonSx}
              />
            </Grid>
          </Grid>
        </Box>
      );
    }

    return null;
  }, [
    userType, 
    formData, 
    errors, 
    banks,
    locations,
    isVerifyingAccount,
    accountVerificationError,
    handleChange, 
    handleUserTypeChange, 
    handleStateChange,
    handleLgaChange,
    handleBankAccountChange,
    handleBankChange,
    handleVerifyAccount,
    showPassword, 
    showConfirmPassword, 
    getCommonSx,
  ]);

  // ============================================
  // RENDER STEPPER
  // ============================================

  const renderStepper = useCallback(() => {
    return (
      <Stepper
        activeStep={activeStep}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        sx={{
          '& .MuiStepLabel-label': {
            color: BRAND_COLORS.textSecondary,
            fontSize: '0.8rem',
            fontWeight: 500,
            '&.Mui-active': { color: BRAND_COLORS.text, fontWeight: 600 },
            '&.Mui-completed': { color: BRAND_COLORS.primary },
          },
          '& .MuiStepIcon-root': {
            color: '#D0D0D0',
            '&.Mui-active': { color: BRAND_COLORS.primary },
            '&.Mui-completed': { color: BRAND_COLORS.primary },
          },
          '& .MuiStepConnector-line': { borderColor: BRAND_COLORS.border },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: BRAND_COLORS.primary },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: BRAND_COLORS.primary },
        }}
      >
        {steps.map((step) => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  }, [activeStep, steps, isMobile]);

  // ============================================
  // PAGE LOADING
  // ============================================

  if (isPageLoading) {
    return <Preloader text="Loading Signup..." size={70} fullScreen={true} />;
  }

  // ============================================
  // VERIFICATION PENDING SCREEN
  // ============================================

  if (signupComplete) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: BRAND_COLORS.background,
          p: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 550,
            width: '100%',
            p: { xs: 2, sm: 4 },
            background: BRAND_COLORS.surface,
            borderRadius: 3,
            border: `1px solid ${BRAND_COLORS.border}`,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}
        >
          <VerificationPending 
            email={registrationData?.email || formData.email}
            userType={registrationData?.userType || userType}
            onResend={handleResendVerification}
            onLogin={handleGoToLogin}
            isResending={isResending}
            resendMessage={resendMessage}
            resendError={resendError}
          />
        </Card>
      </Box>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: BRAND_COLORS.background,
      }}
    >
      {/* HEADER */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: BRAND_COLORS.primary,
          boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              cursor: 'pointer',
            }}
            onClick={() => {
              setActiveStep(0);
              setErrors({});
              logger.debug('Logo clicked - resetting form');
            }}
          >
            <img src={logo} alt="Chemical Database" style={{ height: 40, width: 'auto', borderRadius: 8 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: { xs: 'none', sm: 'block' },
                background: 'linear-gradient(135deg, #FFFFFF, #E8F5E9)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: 0.5,
              }}
            >
              Chemical Database
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* MAIN CONTENT */}
      <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 2, md: 4 } }}>
        <Grid container sx={{ minHeight: '80vh', alignItems: 'center', justifyContent: 'center' }}>
          {/* Left Side - Brand Section */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              pr: 6,
              height: '100%',
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Typography
                variant="h1"
                sx={{
                  color: BRAND_COLORS.text,
                  fontWeight: 800,
                  fontSize: { md: '2.8rem', lg: '3.5rem' },
                  fontFamily: 'Inter, sans-serif',
                  mb: 2,
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                }}
              >
                Create your
                <Box component="span" sx={{ color: BRAND_COLORS.primary, display: 'block' }}>
                  account today
                </Box>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: BRAND_COLORS.textSecondary,
                  fontSize: '1.1rem',
                  mb: 4,
                  maxWidth: 400,
                  lineHeight: 1.7,
                }}
              >
                Join the Chemical Database platform to connect with suppliers and Users in the chemical industry.
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                <Chip
                  icon={<VerifiedUserOutlined sx={{ color: BRAND_COLORS.primary }} />}
                  label="Secure"
                  sx={{
                    backgroundColor: BRAND_COLORS.primaryPale,
                    color: BRAND_COLORS.primary,
                    fontWeight: 500,
                    borderRadius: 2,
                    '& .MuiChip-icon': { color: BRAND_COLORS.primary },
                  }}
                />
                <Chip
                  icon={<HowToRegOutlined sx={{ color: BRAND_COLORS.primary }} />}
                  label="Verified Users"
                  sx={{
                    backgroundColor: BRAND_COLORS.primaryPale,
                    color: BRAND_COLORS.primary,
                    fontWeight: 500,
                    borderRadius: 2,
                    '& .MuiChip-icon': { color: BRAND_COLORS.primary },
                  }}
                />
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  pt: 3,
                  borderTop: `1px solid ${BRAND_COLORS.border}`,
                }}
              >
                <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary }}>
                  Already have an account?
                </Typography>
                <Button
                  component={Link}
                  to="/RawAuth"
                  sx={{
                    color: BRAND_COLORS.primary,
                    textTransform: 'none',
                    fontWeight: 600,
                    justifyContent: 'flex-start',
                    p: 0,
                    fontSize: '1rem',
                    '&:hover': {
                      background: 'transparent',
                      color: BRAND_COLORS.primaryDark,
                    },
                  }}
                >
                  Sign In →
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Right Side - Signup Form */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Card
              ref={formRef}
              sx={{
                width: '100%',
                maxWidth: 600,
                background: BRAND_COLORS.surface,
                borderRadius: 3,
                border: `1px solid ${BRAND_COLORS.border}`,
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                position: 'relative',
                overflow: 'hidden',
                maxHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent 
                sx={{ 
                  p: { xs: 2.5, sm: 3.5, md: 4 },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {/* Mobile Header */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 2, mb: 3, flexShrink: 0 }}>
                  <Box
                    component="img"
                    src={logo}
                    alt="Chemical Database"
                    sx={{ width: 45, height: 'auto', borderRadius: 2 }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      color: BRAND_COLORS.text,
                      fontWeight: 700,
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    Sign Up
                  </Typography>
                </Box>

                {/* Error/Success Messages */}
                {serverError && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 2, borderRadius: 2, border: '1px solid #FFE5E5', flexShrink: 0 }}
                    onClose={() => setServerError('')}
                  >
                    <AlertTitle sx={{ fontWeight: 600 }}>Registration Failed</AlertTitle>
                    {serverError}
                  </Alert>
                )}

                {successMessage && !signupComplete && (
                  <Alert 
                    severity="success" 
                    sx={{ mb: 2, borderRadius: 2, border: '1px solid #D4EDDA', flexShrink: 0 }}
                  >
                    <AlertTitle sx={{ fontWeight: 600 }}>Success</AlertTitle>
                    {successMessage}
                  </Alert>
                )}

                {/* Stepper */}
                <Box sx={{ flexShrink: 0, mb: 2 }}>
                  {renderStepper()}
                </Box>

                {/* Step Content */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                    pr: 1,
                    mb: 2,
                    '&::-webkit-scrollbar': {
                      width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#F0F0F0',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: BRAND_COLORS.primary,
                      borderRadius: '4px',
                    },
                  }}
                  onKeyPress={handleKeyPress}
                >
                  {renderStepContent(activeStep)}
                </Box>

                {/* Terms and Conditions */}
                {activeStep === steps.length - 1 && (
                  <Box sx={{ flexShrink: 0, mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          sx={{
                            color: '#D0D0D0',
                            '&.Mui-checked': {
                              color: BRAND_COLORS.primary,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary }}>
                          I agree to the{' '}
                          <Button
                            component={Link}
                            to="/terms"
                            sx={{
                              color: BRAND_COLORS.primary,
                              textTransform: 'none',
                              p: 0,
                              minWidth: 'auto',
                              fontWeight: 600,
                              '&:hover': {
                                background: 'transparent',
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Terms & Conditions
                          </Button>
                          {' and '}
                          <Button
                            component={Link}
                            to="/privacy"
                            sx={{
                              color: BRAND_COLORS.primary,
                              textTransform: 'none',
                              p: 0,
                              minWidth: 'auto',
                              fontWeight: 600,
                              '&:hover': {
                                background: 'transparent',
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Privacy Policy
                          </Button>
                        </Typography>
                      }
                    />
                  </Box>
                )}

                {/* Navigation Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 2,
                    gap: 2,
                    flexShrink: 0,
                    pt: 2,
                    borderTop: `1px solid ${BRAND_COLORS.border}`,
                  }}
                >
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0 || isLoading}
                    variant="outlined"
                    sx={{
                      color: BRAND_COLORS.textSecondary,
                      borderColor: BRAND_COLORS.border,
                      borderRadius: 2,
                      px: 3,
                      py: 1.2,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: BRAND_COLORS.primary,
                        color: BRAND_COLORS.primary,
                        background: BRAND_COLORS.primaryPale,
                      },
                      '&.Mui-disabled': {
                        borderColor: BRAND_COLORS.border,
                        color: '#D0D0D0',
                      },
                    }}
                  >
                    <ArrowBack sx={{ mr: 1, fontSize: 18 }} />
                    Back
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={isLoading}
                    sx={{
                      px: 4,
                      py: 1.2,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${BRAND_COLORS.primary} 0%, ${BRAND_COLORS.primaryDark} 100%)`,
                      color: BRAND_COLORS.surface,
                      fontWeight: 600,
                      textTransform: 'none',
                      transition: 'all 0.3s ease',
                      boxShadow: `0 2px 12px ${alpha(BRAND_COLORS.primary, 0.3)}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 20px ${alpha(BRAND_COLORS.primary, 0.4)}`,
                      },
                      '&:active': {
                        transform: 'scale(0.98)',
                      },
                      '&.Mui-disabled': {
                        background: '#D0D0D0',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : activeStep === steps.length - 1 ? (
                      'Complete Registration'
                    ) : (
                      <>
                        Next
                        <ArrowForward sx={{ ml: 1, fontSize: 18 }} />
                      </>
                    )}
                  </Button>
                </Box>

                {/* Login Link for Mobile */}
                <Box
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    mt: 1.5,
                    textAlign: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="body2" sx={{ color: BRAND_COLORS.textSecondary }}>
                    Already have an account?{' '}
                    <Button
                      component={Link}
                      to="/RawAuth"
                      sx={{
                        color: BRAND_COLORS.primary,
                        textTransform: 'none',
                        fontWeight: 600,
                        p: 0,
                        minWidth: 'auto',
                        '&:hover': {
                          background: 'transparent',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Sign In
                    </Button>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default SignupPage;